import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { EntityProfileIndustry, EntityProfileType } from "@common/types/entity";
import {
    ArchiveFilter,
    matchesArchiveFilter,
} from "../templates/archiveFilter";

export interface ChartOfAccountsTemplateFilterable {
    basis: AccountingLedgerBasis[];
    industries: EntityProfileIndustry[];
    entityLegalType: EntityProfileType;
    archivedAt: Date | null;
}

interface FilterChartOfAccountsTemplateRowsParams<
    TRow extends ChartOfAccountsTemplateFilterable,
> {
    rows: TRow[];
    selectedIndustries: EntityProfileIndustry[];
    selectedBases: AccountingLedgerBasis[];
    selectedLegalTypes: EntityProfileType[];
    archiveFilter: ArchiveFilter;
}

/**
 * Filters chart-of-accounts template rows by the Industry / Basis / Entity-type
 * selections and the Active / Archived / All status. Each filter is skipped
 * when its selection is empty. Industry and Basis match when the template
 * overlaps any selected value (so a template covering the full enum set
 * naturally matches any single selection); an empty template array matches
 * nothing while that filter is active. Entity type is an exact membership
 * check. Returns the input unchanged when nothing is active.
 */
export function filterChartOfAccountsTemplateRows<
    TRow extends ChartOfAccountsTemplateFilterable,
>({
    rows,
    selectedIndustries,
    selectedBases,
    selectedLegalTypes,
    archiveFilter,
}: FilterChartOfAccountsTemplateRowsParams<TRow>): TRow[] {
    const hasIndustryFilter = selectedIndustries.length > 0;
    const hasBasisFilter = selectedBases.length > 0;
    const hasLegalTypeFilter = selectedLegalTypes.length > 0;

    if (
        !hasIndustryFilter &&
        !hasBasisFilter &&
        !hasLegalTypeFilter &&
        archiveFilter === "all"
    ) {
        return rows;
    }

    return rows.filter((row) => {
        const industryOk =
            !hasIndustryFilter ||
            row.industries.some((industry) =>
                selectedIndustries.includes(industry),
            );
        const basisOk =
            !hasBasisFilter ||
            row.basis.some((basis) => selectedBases.includes(basis));
        const legalTypeOk =
            !hasLegalTypeFilter ||
            selectedLegalTypes.includes(row.entityLegalType);
        const isStatusMatch = matchesArchiveFilter({
            isArchived: row.archivedAt !== null,
            archiveFilter,
        });
        return industryOk && basisOk && legalTypeOk && isStatusMatch;
    });
}
